import { caughtErrorMessage } from "./errors";
import { err, ok, type Result } from "./result";

declare const httpUrlBrand: unique symbol;

export type HttpUrl = string & { readonly [httpUrlBrand]: "HttpUrl" };

export type ParsedHttpUrl = {
  readonly input: HttpUrl;
  readonly url: URL;
};

export type HttpUrlParseError =
  | { readonly kind: "missingUrl" }
  | { readonly kind: "invalidUrl"; readonly reason: string }
  | { readonly kind: "unsupportedProtocol"; readonly protocol: string };

export type PromptedHttpUrl =
  | { readonly kind: "cancelled" }
  | { readonly kind: "ready"; readonly value: HttpUrl }
  | { readonly kind: "invalid"; readonly error: HttpUrlParseError };

export const httpUrlParseErrorMessage = (error: HttpUrlParseError): string => {
  switch (error.kind) {
    case "missingUrl":
      return "Image URL is required.";
    case "invalidUrl":
      return "Enter a valid image URL.";
    case "unsupportedProtocol":
      return "Use an http or https image URL.";
  }
};

export const parseHttpUrl = (value: string): Result<ParsedHttpUrl, HttpUrlParseError> => {
  const input = value.trim();
  if (input === "") return err({ kind: "missingUrl" });

  let url: URL;
  try {
    url = new URL(input);
  } catch (error) {
    return err({
      kind: "invalidUrl",
      reason: caughtErrorMessage(error, "could not parse URL"),
    });
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return err({ kind: "unsupportedProtocol", protocol: url.protocol });
  }

  return ok({ input: input as HttpUrl, url });
};

export const promptedHttpUrl = (value: string | null): PromptedHttpUrl => {
  if (value === null) return { kind: "cancelled" };
  const parsed = parseHttpUrl(value);
  return parsed.ok
    ? { kind: "ready", value: parsed.value.input }
    : { kind: "invalid", error: parsed.error };
};

export type ImageInsertCommand =
  | { readonly kind: "cancelled" }
  | { readonly kind: "ready"; readonly src: HttpUrl }
  | { readonly kind: "invalid"; readonly error: HttpUrlParseError };

export const imageInsertCommandFromPrompt = (value: string | null): ImageInsertCommand => {
  const prompted = promptedHttpUrl(value);
  switch (prompted.kind) {
    case "cancelled":
      return { kind: "cancelled" };
    case "invalid":
      return { kind: "invalid", error: prompted.error };
    case "ready":
      return { kind: "ready", src: prompted.value };
  }
};

export type ImageInsertDraftState =
  | { readonly kind: "closed" }
  | { readonly kind: "editing"; readonly value: string; readonly error: HttpUrlParseError | null };

export type ImageInsertDraftAction =
  | { readonly type: "opened" }
  | { readonly type: "valueInput"; readonly value: string }
  | { readonly type: "failed"; readonly error: HttpUrlParseError }
  | { readonly type: "inserted" }
  | { readonly type: "cancelled" };

export const emptyImageInsertDraftState = (): ImageInsertDraftState => ({ kind: "closed" });

export const nextImageInsertDraftState = (
  state: ImageInsertDraftState,
  action: ImageInsertDraftAction,
): ImageInsertDraftState => {
  switch (action.type) {
    case "opened":
      return state.kind === "editing" ? state : { kind: "editing", value: "", error: null };
    case "valueInput":
      return { kind: "editing", value: action.value, error: null };
    case "failed":
      return state.kind === "editing"
        ? { ...state, error: action.error }
        : { kind: "editing", value: "", error: action.error };
    case "inserted":
    case "cancelled":
      return { kind: "closed" };
  }
};

export const imageInsertDraftCommand = (state: ImageInsertDraftState): ImageInsertCommand =>
  state.kind === "editing" ? imageInsertCommandFromPrompt(state.value) : { kind: "cancelled" };
